import { useRecoilState, useRecoilValue } from "recoil";
import { parapassBooksProgressAtom, parapassQuizDataAtom, parapassUsageDataAtom, userAtom } from "@state/State";
import { IParapassBooksProgress, IParapassQuizData, IParapassUsageData, IUser } from "@interfaces/Interfaces";
import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as DatabaseService from "@controllers/DatabaseService";
import DataController from "@controllers/DataController";
import { USAGE_DATA_REFRESH_INTERVAL } from "../../Constants";
import { useLoggedIn } from "@hooks/auth/useLoggedIn";
import _ from "lodash";

type RefreshParapassData = {
  refreshParapassQuizData: (user: IUser) => Promise<IParapassQuizData[]>;
  refreshParapassUsageData: (user: IUser) => Promise<void>;
  refreshParapassBooksProgress: (user: IUser) => Promise<void>;
};

const fetchParapassQuizData = async (user: IUser): Promise<IParapassQuizData[]> => {
  if (!user || !user.userData) {
    throw new Error("User is required");
  }

  const data = (await DatabaseService.getParapassQuizData(user)) ?? [];
  return data;
};

const fetchParapassUsageData = async (user: IUser): Promise<IParapassUsageData[]> => {
  if (!user || !user.userData) {
    throw new Error("User is required");
  }

  const data = (await DatabaseService.getParapassUsageData(user)) ?? [];

  return data.map((item: IParapassUsageData) => ({
    ...item,
    title: item.ContentTitle,
  }));
};

const fetchParapassBooksProgress = async (user: IUser): Promise<IParapassBooksProgress[]> => {
  if (!user || !user.userData) {
    throw new Error("User is required");
  }

  const data = (await DatabaseService.getParapassBooksProgress(user)) ?? [];
  return data;
};

export const useRefreshParapassData = (shouldLoad?: boolean): RefreshParapassData => {
  const user = useRecoilValue(userAtom);
  const [parapassQuizData, setParapassQuizData] = useRecoilState<IParapassQuizData[] | null>(parapassQuizDataAtom);
  const [parapassUsageData, setParapassUsageData] = useRecoilState<IParapassUsageData[] | null>(parapassUsageDataAtom);
  const [parapassBooksProgress, setParapassBooksProgress] = useRecoilState<IParapassBooksProgress[] | null>(
    parapassBooksProgressAtom
  );

  const loggedIn = useLoggedIn();
  const queryClient = useQueryClient();

  const isEnabled = Boolean(loggedIn && user && user.userData && shouldLoad);

  const { data: refreshedQuizData } = useQuery({
    queryKey: ["parapass-quiz-data", user?.userData?.contactID],
    queryFn: () => fetchParapassQuizData(user!),
    enabled: isEnabled,
    refetchInterval: isEnabled ? USAGE_DATA_REFRESH_INTERVAL : false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const { data: refreshedUsageData } = useQuery({
    queryKey: ["parapass-usage-data", user?.userData?.contactID],
    queryFn: () => fetchParapassUsageData(user!),
    enabled: isEnabled,
    refetchInterval: isEnabled ? USAGE_DATA_REFRESH_INTERVAL : false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const { data: refreshedBooksProgress } = useQuery({
    queryKey: ["parapass-books-progress", user?.userData?.contactID],
    queryFn: () => fetchParapassBooksProgress(user!),
    enabled: isEnabled,
    refetchInterval: isEnabled ? USAGE_DATA_REFRESH_INTERVAL : false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!user || !user.userData) {
      queryClient.removeQueries({ queryKey: ["parapass-quiz-data"] });
      queryClient.removeQueries({ queryKey: ["parapass-usage-data"] });
    }
  }, [user, queryClient]);

  useEffect(() => {
    if (refreshedQuizData) {
      const quizDataIsEqual = _.isEqual(parapassQuizData, refreshedQuizData);

      if (!quizDataIsEqual) {
        DataController.saveParapassUsageData(refreshedQuizData);
        setParapassQuizData(refreshedQuizData);
      }
    }
  }, [refreshedQuizData, parapassQuizData, setParapassQuizData]);

  useEffect(() => {
    if (refreshedUsageData) {
      const usageDataIsEqual = _.isEqual(parapassUsageData, refreshedUsageData);

      if (!usageDataIsEqual) {
        setParapassUsageData(refreshedUsageData);
      }
    }
  }, [refreshedUsageData, parapassUsageData, setParapassUsageData]);

  useEffect(() => {
    if (refreshedBooksProgress) {
      const booksProgressIsEqual = _.isEqual(parapassBooksProgress, refreshedBooksProgress);

      if (!booksProgressIsEqual) {
        setParapassBooksProgress(refreshedBooksProgress);
      }
    }
  }, [refreshedBooksProgress, parapassBooksProgress, setParapassBooksProgress]);

  const refreshParapassQuizData = useCallback(
    async (_user: IUser): Promise<IParapassQuizData[]> => {
      try {
        if (!_user || !_user.userData) {
          return [];
        }

        const data = await fetchParapassQuizData(_user);
        const quizDataIsEqual = _.isEqual(parapassQuizData, data);

        if (!quizDataIsEqual) {
          await DataController.saveParapassUsageData(data);
          setParapassQuizData(data);
        }

        queryClient.invalidateQueries({ queryKey: ["parapass-quiz-data", _user.userData.contactID] });

        return quizDataIsEqual ? parapassQuizData ?? [] : data;
      } catch (error) {
        console.log(error);
        return [];
      }
    },
    [parapassQuizData, setParapassQuizData, queryClient]
  );

  const refreshParapassUsageData = useCallback(
    async (_user: IUser): Promise<void> => {
      try {
        if (!_user || !_user.userData) {
          return;
        }

        const data = await fetchParapassUsageData(_user);
        const usageDataIsEqual = _.isEqual(parapassUsageData, data);

        if (!usageDataIsEqual) {
          setParapassUsageData(data);
        }

        queryClient.invalidateQueries({ queryKey: ["parapass-usage-data", _user.userData.contactID] });
      } catch (error) {
        console.log(error);
      }
    },
    [parapassUsageData, setParapassUsageData, queryClient]
  );

  const refreshParapassBooksProgress = useCallback(
    async (_user: IUser): Promise<void> => {
      try {
        if (!_user || !_user.userData) {
          return;
        }

        const data = await fetchParapassBooksProgress(_user);
        const booksProgressIsEqual = _.isEqual(parapassBooksProgress, data);

        if (!booksProgressIsEqual) {
          setParapassBooksProgress(data);
        }

        queryClient.invalidateQueries({ queryKey: ["parapass-books-progress", _user.userData.contactID] });
      } catch (error) {
        console.log(error);
      }
    },
    [parapassBooksProgress, setParapassBooksProgress, queryClient]
  );

  return {
    refreshParapassQuizData,
    refreshParapassUsageData,
    refreshParapassBooksProgress,
  };
};
